import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    showOnlyFixes: false,
    // Fix in place by default: the compatibility fix is a 2-byte header patch
    // (same audio, lossless, instant), so a "[fixed]" copy buys nothing but
    // disk, clutter and a second "Use fixed files" click. Loudness (which
    // re-encodes) defaults the other way. Persisted per-user thereafter.
    backupOriginalFiles: false,
    fixIncompatibleFiles: true,
    convertAiffToWav: true,
    conversionFormat: 'wav', // 'wav' or 'mp3'
    activeFilters: [], // Array of active filters: 'compatible', 'incompatible', 'fixed', 'depends'
    activeFileTypes: [], // none selected = show all; click a type to filter TO it (include semantics)
    useStatusBadges: true, // true for text badges, false for icon-only
    // Folder-source list order: 'name' = flat A-Z by displayed "Artist - Title";
    // 'folder' = grouped under subfolder headers (preserves the user's own
    // folder organization). Only meaningful for folder sources.
    listGrouping: 'name',
    // Non-folder sources (playlist / library / picked files): 'added' keeps
    // the source's own order (playlist position, library sort, pick order);
    // 'name' re-sorts A-Z by the displayed label at render time.
    listSort: 'added'
};

const fixSettingsSlice = createSlice({
    name: 'fixSettings',
    initialState,
    reducers: {
        setShowOnlyFixes (state, action) {
            state.showOnlyFixes = action.payload;
        },
        setBackupOriginalFiles (state, action) {
            state.backupOriginalFiles = action.payload;
        },
        setFixIncompatibleFiles (state, action) {
            state.fixIncompatibleFiles = action.payload;
        },
        setConvertAiffToWav (state, action) {
            state.convertAiffToWav = action.payload;
        },
        setConversionFormat (state, action) {
            state.conversionFormat = action.payload;
        },
        setActiveFilter(state, action) {
            // For 'all', clear all filters
            if (action.payload === 'all') {
                state.activeFilters = [];
            } else {
                // Toggle filter in/out of array
                const index = state.activeFilters.indexOf(action.payload);
                if (index > -1) {
                    state.activeFilters = state.activeFilters.filter(f => f !== action.payload);
                } else {
                    state.activeFilters = [...state.activeFilters, action.payload];
                }
            }
        },
        setFileTypeFilter(state, action) {
            // For 'all', clear all file type filters
            if (action.payload === 'all') {
                state.activeFileTypes = [];
            } else {
                // Toggle file type in/out of array
                const index = state.activeFileTypes.indexOf(action.payload);
                if (index > -1) {
                    state.activeFileTypes = state.activeFileTypes.filter(f => f !== action.payload);
                } else {
                    state.activeFileTypes = [...state.activeFileTypes, action.payload];
                }
            }
        },
        setUseStatusBadges(state, action) {
            state.useStatusBadges = action.payload;
        },
        setListGrouping(state, action) {
            state.listGrouping = action.payload === 'folder' ? 'folder' : 'name';
        },
        setListSort(state, action) {
            state.listSort = action.payload === 'name' ? 'name' : 'added';
        },
        // Merge persisted settings loaded from localStorage at startup
        // (whitelisted subset — see settingsPersistenceConfig).
        hydrate(state, action) {
            Object.assign(state, action.payload || {});
        }
    },
});

export const {
    setShowOnlyFixes,
    setBackupOriginalFiles,
    setFixIncompatibleFiles,
    setConvertAiffToWav,
    setConversionFormat,
    setActiveFilter,
    setFileTypeFilter,
    setUseStatusBadges,
    setListGrouping,
    setListSort,
    hydrate
} = fixSettingsSlice.actions;

export default fixSettingsSlice.reducer;
